import { setInObjectByPath } from '@symbo.ls/utils'

export async function openEditor(type, key) {
  const item = (key ? this.getItem(key, type) : null) || this.getSelected()?.state
  if (!item) return

  const scope = this.getCanvasScope()
  const path = scope.hoverLayer || []
  this.syncCanvas()

  // The persisted map, not `scope.showLayersSchema || {}`: after a reload
  // the scope has no map yet, and a write that started from `{}` persisted
  // a map that had lost every other row's entry (see getShowLayersSchema).
  const showLayersSchema = getShowLayersSchema.call(this)
  const componentShowLayersSchema = JSON.parse(JSON.stringify(showLayersSchema?.[item.key] || {}))

  if (!componentShowLayersSchema) return
  const updateShowLayers = (schema, path) => {
    let current = schema
    current.showLayers = true
    path?.some((key) => {
      if (key[0] !== key[0].toLowerCase()) {
        if (current[key] === undefined) {
          current[key] = {}
        }
        current[key].showLayers = true
        current = current[key]
      } else {
        return true
      }
    })
  }

  updateShowLayers(componentShowLayersSchema, path)

  const isActive = this.call('arraysEqual', path, scope.activeLayer || [])
  showLayersSchema[item.key] = componentShowLayersSchema
  scope.showLayersSchema = showLayersSchema

  if (isActive) {
    scope.activeLayer = null
  } else {
    scope.activeLayer = path.filter((key) => key[0] !== key[0].toLowerCase())
  }

  if (scope.componentEditor === 'code') {
    this.changeCodePriority('component')

    await this.getComponentCodeEditor().update(
      {},
      new (this.call('sameOriginTop') || window).Object({
        preventUpdate: true,
        allowCodeSelection: true
      })
    )
  }

  this.syncCanvas()

  const isExplorerActive = Boolean(scope.explorer)
  const isExplorerArtboardsActive = scope.explorer === 'explorer:ARTBOARD_TYPES'

  if (isExplorerActive && !isExplorerArtboardsActive) {
    this.setWindow('explorer', 'explorer:ARTBOARD_TYPES')
  }

  if (!scope.explorer && !scope.editorActive) return

  const t = setTimeout(async () => {
    const componentLayer = isExplorerActive
      ? this.call('findFileSidebarItem', item.type, item.key)
      : this.getQuickEditor()

    if (!componentLayer?.state) {
      clearTimeout(t)
      return
    }

    await componentLayer.state.update({
      showLayers: true
    })
    componentLayer.node?.scrollIntoViewIfNeeded?.()
    // componentLayer.node.focus()
    clearTimeout(t)
  }, 350)
}

// Was never registered — `el.call('getLayerPath')` silently resolved to
// `undefined`, which crashed FilesSidebarListItem.onStateUpdate's
// `path.push('showLayers')` on every layer expand/collapse (the onInit
// call site guards with `|| []`, onStateUpdate didn't). `showLayersSchema`
// (see openEditor above) is keyed at the TOP level by the sidebar row's
// own item key — `showLayersSchema[item.key].showLayers` — so the path
// into it for a top-level FilesSidebarListItem is just its own key.
export function getLayerPath() {
  return [this.state.key]
}

// The files-sidebar layer-visibility store — ONE map on the Canvas scope,
// keyed by the sidebar row's item key (the pages and the components sections
// share it), nested by PascalCase layer key, `showLayers` at every level: the
// shape `openEditor` writes above. `syncCanvas` carries it into `<app>__data`
// localStorage (an allowed key of `getCleanCanvasScope`), which is what makes
// it survive a reload — but nothing seeds the scope from that copy at boot,
// so a scope with no map yet resolves to the persisted copy the scope is
// seeded from (`getCanvasScopeFromStorageOrData`, the canonical "not built
// yet" read) and installs it, once, by reference. Resolving to `{}` instead
// restored nothing after a reload and let the next writer (`openEditor`, a
// collapse) persist a map that had lost every other entry. The scene can also
// build its scope AFTER the editor chrome mounts (in-shell mount order — see
// `setCanvasScope`): with no scope at all the persisted copy is returned as
// is. Never `undefined` — `FilesSidebarListItem.onInit` reads it through
// `getInObjectByPath(schema, [rowKey, 'showLayers'])`.
export function getShowLayersSchema() {
  const scope = this.getCanvasScope()
  if (scope?.showLayersSchema) return scope.showLayersSchema
  const persisted = this.getCanvasScopeFromStorageOrData()?.showLayersSchema
  if (!scope) return persisted || {}
  if (persisted) scope.showLayersSchema = persisted
  return scope.showLayersSchema || {}
}

// `FilesSidebarListItem.onStateUpdate`'s sink — never registered before
// (WS-FILES-SAVE-LAYERS-VISIBILITY-STATE-MISSING-1): `el.call` resolved it to
// `undefined`, so an expand/collapse was never persisted and every reload came
// back at whatever `openEditor` had last written. `path` is `getLayerPath()` +
// 'showLayers' — `[rowKey, 'showLayers']` for a top-level row. Writes through
// `syncCanvas` with the SAME map object the scope holds (the
// `FilesSidebarList.children` idiom), so the scope, the `clients` mirror,
// localStorage and the socket delta all carry one map. No scope yet → no-op,
// the `setCanvasScope` contract: the caller's next write after the scene
// mounts lands.
export function saveLayersVisibilityState(path, value) {
  if (!Array.isArray(path) || !path.length) return null
  const scope = this.getCanvasScope()
  if (!scope) return null
  const showLayersSchema = getShowLayersSchema.call(this)
  setInObjectByPath(showLayersSchema, path, Boolean(value))
  this.syncCanvas({ showLayersSchema })
  return showLayersSchema
}
